import { useEffect, useRef, useState } from "react";
import { Alert } from "reactstrap";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { Link, useLocation } from "react-router-dom";
import useTypedNavigate from "../hooks/typed-navigate";
import { loginWithGoogle } from "../actions/user";
import { isGoogleSignInError, retryGoogleSignIn } from "../services/cognito";
import { authUrl, DEFAULT_AFTER_AUTH } from "../utils/authRedirect";
import { useSeo } from "../hooks/useSeo";

type AppDispatch = ThunkDispatch<RootReducerState, unknown, AuthenticationAction>;

type View =
  | { kind: "working" }
  | { kind: "restarting" }
  | { kind: "error"; message: string; next: string | null };

/**
 * Fixed copy only. The callback URL is attacker-controllable, so its
 * `error_description` is never rendered — it would let anyone put their own
 * words on a dejachart.com page.
 */
function errorMessage(error: unknown): string {
  if (isGoogleSignInError(error)) {
    switch (error.kind) {
    case "provider":
      return error.code === "access_denied"
        ? "Google sign-in was cancelled. You can try again, or use your email and password."
        : "Google sign-in didn't go through. Please try again, or use your email and password.";
    case "linking":
      return "We couldn't finish linking your Google account. Please try again, or log in with your email and password.";
    case "state":
      return "This sign-in link has expired, or it was opened in a different tab. Please start again.";
    default:
      break;
    }
  }
  return "We couldn't finish signing you in with Google. Please try again.";
}

/** Landing page for Cognito's redirect after Google (`/auth/callback`). */
export default function GoogleCallback() {
  useSeo({ title: "Signing In", path: "/auth/callback", noindex: true });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useTypedNavigate();
  const location = useLocation();
  const [view, setView] = useState<View>({ kind: "working" });
  // The code is single-use: a second run (StrictMode's dev double-effect, a
  // re-render with a new `navigate`) would exchange it again and fail.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(location.search);
    void (async () => {
      try {
        const { next } = await dispatch(loginWithGoogle(params));
        navigate(next ?? DEFAULT_AFTER_AUTH, { replace: true });
      } catch (error) {
        if (isGoogleSignInError(error) && error.kind === "linking") {
          setView({ kind: "restarting" });
          try {
            // Resolves true once the browser is on its way back to Google.
            if (await retryGoogleSignIn(error.next)) return;
          } catch (retryError) {
            console.error(retryError);
          }
        }
        console.error(error);
        setView({
          kind: "error",
          message: errorMessage(error),
          next: isGoogleSignInError(error) ? error.next : null,
        });
      }
    })();
  }, [dispatch, location.search, navigate]);

  if (view.kind === "error") {
    return (
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-md-auto login-container">
            <h2>Sign-in didn't finish</h2>
            <Alert color="danger">{view.message}</Alert>
            <p>
              <Link to={authUrl("/login", view.next ?? "")}>Back to log in</Link>
              {" · "}
              <Link to={authUrl("/signup", view.next ?? "")}>Sign up</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-md-center">
        <div className="col-md-auto login-container" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />{" "}
          {view.kind === "restarting" ? "Finishing sign-in…" : "Signing you in…"}
        </div>
      </div>
    </div>
  );
}
